import { useEffect, useRef, useState, useCallback } from 'react';
import { useAbortController } from './useAbortController';
import { useApiFetch } from './useApiFetch';
import { useAuthContext } from '../contexts/AuthContext';
import type {
  JobListItem,
  JobsResponse,
  JobsScope,
  JobStatusCounts,
} from '../types/jobs';
import type { JobStatus } from '../types/monitoring';

interface UseJobsResult {
  jobs: JobListItem[];
  isLoading: boolean;
  error: Error | null;
  total: number;
  statusCounts: JobStatusCounts;
  refetch: () => void;
}

interface UseJobsOptions {
  scope: JobsScope;
  page?: number;
  limit?: number;
  search?: string;
  status?: JobStatus | null;
  waiting?: boolean;
}

export function useJobs(options: UseJobsOptions): UseJobsResult {
  const {
    scope,
    page = 1,
    limit = 50,
    search = '',
    status = null,
    waiting = false,
  } = options;

  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<JobStatusCounts>({});
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const { user } = useAuthContext();
  const callerSub = user?.sub ?? null;
  const { error, apiFetch } = useApiFetch();
  const { getAbortController } = useAbortController();
  // Monotonic request id: a fetch already past `await response.json()` sits in
  // the microtask queue and would still call setState even when its abort
  // signal fired afterwards. We bump this on each issue and ignore any reply
  // that doesn't match the latest id.
  const fetchIdRef = useRef(0);

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      const myId = ++fetchIdRef.current;
      setIsDataLoading(true);
      const abortController = getAbortController();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (scope === 'mine') {
        if (callerSub === null) {
          // Identity not ready yet — refuse to silently broaden "mine" to "all".
          setJobs([]);
          setTotal(0);
          setStatusCounts({});
          setIsDataLoading(false);
          return;
        }
        params.append('created_by', callerSub);
      }

      if (search.trim()) {
        params.append('search', search.trim());
      }
      if (status) {
        params.append('status', status);
      }
      if (waiting) {
        params.append('waiting', '1');
      }

      const response = await apiFetch(`/meta/jobs?${params}`, {
        signal: abortController.signal,
      });

      const isStale = () =>
        myId !== fetchIdRef.current || abortController.signal.aborted;

      if (!response) {
        if (!isStale()) {
          setIsDataLoading(false);
        }
        return;
      }

      const data: JobsResponse = await response.json();
      if (isStale()) return;
      setJobs(data.items);
      setTotal(data.total);
      setStatusCounts(data.status_counts ?? {});
      setIsDataLoading(false);
    };

    void fetchJobs();
  }, [
    scope,
    page,
    limit,
    search,
    status,
    waiting,
    callerSub,
    refetchTrigger,
    apiFetch,
    getAbortController,
  ]);

  return {
    jobs,
    isLoading: isDataLoading,
    error,
    total,
    statusCounts,
    refetch,
  };
}
